import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { CheckCircle, AlertTriangle, XCircle, X } from "lucide-react";
import "./styles.scss";

export type ToastType = "success" | "warning" | "error";

export interface ToastOptions {
  type: ToastType;
  title?: string;
  message?: string;
  duration?: number; // ms
}

interface Toast extends ToastOptions {
  id: string;
  createdAt: number;
}

// Simple pub/sub toast store
const toastStore = (() => {
  let toasts: Toast[] = [];
  const listeners = new Set<(t: Toast[]) => void>();

  const notify = () => listeners.forEach((l) => l([...toasts]));

  const add = (toast: Toast) => {
    toasts = [toast, ...toasts];
    notify();
  };

  const remove = (id: string) => {
    toasts = toasts.filter((t) => t.id !== id);
    notify();
  };

  const subscribe = (fn: (t: Toast[]) => void) => {
    listeners.add(fn);
    return () => listeners.delete(fn);
  };

  return { add, remove, subscribe };
})();

// Ensure a host root exists for toasts and mount the host component once
let hostMounted = false;
let hostReady = false;
const ensureHost = () => {
  if (hostMounted) return;
  const el = document.createElement("div");
  el.id = "toast-portal-root";
  document.body.appendChild(el);
  const root = createRoot(el);
  root.render(
    <ToastHost
      onReady={() => {
        hostReady = true;
      }}
    />
  );
  hostMounted = true;
};

// Public API
export const showToast = (opts: ToastOptions) => {
  if (typeof window === "undefined") return;
  ensureHost();

  // Wait for host to be ready before adding toast
  const addToast = () => {
    const id = Math.random().toString(36).slice(2);
    const duration = opts.duration ?? 5000;
    toastStore.add({ id, createdAt: Date.now(), ...opts, duration });
    window.setTimeout(() => toastStore.remove(id), duration);
  };

  if (hostReady) {
    addToast();
  } else {
    // Wait a bit for the host to be ready
    setTimeout(addToast, 100);
  }
};

export const showSuccessToast = (
  message?: string,
  title?: string,
  duration?: number
) =>
  showToast({
    type: "success",
    message: message ?? "Operation successful",
    title: title ?? "Success",
    duration,
  });
export const showWarningToast = (
  message?: string,
  title?: string,
  duration?: number
) =>
  showToast({
    type: "warning",
    message: message ?? "Please review the details",
    title: title ?? "Warning",
    duration,
  });
export const showErrorToast = (
  message?: string,
  title?: string,
  duration?: number
) => {
  console.log("showErrorToast called with:", { message, title, duration });
  showToast({
    type: "error",
    message: message ?? "Something went wrong",
    title: title ?? "Error",
    duration,
  });
};

// Host component that renders the list of toasts
const ToastHost: React.FC<{ onReady?: () => void }> = ({ onReady }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  useEffect(() => {
    const unsubscribe = toastStore.subscribe(setToasts);
    // Signal that the host is ready to receive toasts
    if (onReady) onReady();
    return () => {
      // ensure cleanup returns void, not boolean
      void unsubscribe();
    };
  }, [onReady]);
  return (
    <div className="toast-container">
      {toasts.map((t) => (
        <ToastItem
          key={t.id}
          toast={t}
          onClose={() => toastStore.remove(t.id)}
        />
      ))}
    </div>
  );
};

const iconFor = (type: ToastType) => {
  switch (type) {
    case "success":
      return <CheckCircle className="toast-icon toast-icon--success" />;
    case "warning":
      return <AlertTriangle className="toast-icon toast-icon--warning" />;
    case "error":
    default:
      return <XCircle className="toast-icon toast-icon--error" />;
  }
};

const ToastItem: React.FC<{ toast: Toast; onClose: () => void }> = ({
  toast,
  onClose,
}) => {
  console.log("ToastItem rendering toast:", toast);
  return (
    <div
      className={`toast toast--${toast.type}`}
      role="status"
      aria-live="polite"
    >
      <div className="toast-left">{iconFor(toast.type)}</div>
      <div className="toast-body">
        {toast.title && <div className="toast-title">{toast.title}</div>}
        {toast.message && <div className="toast-message">{toast.message}</div>}
        <div
          className="toast-progress"
          style={{ animationDuration: `${(toast as any).duration ?? 5000}ms` }}
        />
      </div>
      <button
        className="toast-close"
        aria-label="Close notification"
        onClick={onClose}
      >
        <X size={16} />
      </button>
    </div>
  );
};

export default {};
